import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import { useDataQuery } from "@hooks/useData/useDataQuery";
import useGzLog from "@hooks/useGzLog";
import { selectData, softDelete, supabase } from "@utils/serverClient";
import { undefinedToNull } from "lib/utils";
import { InsertLayout, Layout, Template, UpdateLayout } from "types";

export const useLayouts = () => {
  const { log } = useGzLog()
  const { organization } = useAuth();
  const orgId = organization?.id;

  const queryKey = ["layouts", orgId];

  const {
    status: layoutsFetchStatus,
    data: layouts,
    refetch: getLayouts,
  } = useDataQuery({
    queryKey,
    queryFn: async () => {
      if (!orgId) return;
      const { data, error } = await selectData({
        table: "layout",
        columns: `*`,
      })
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });
      if (error || !data) return;
      return data;
    },
  });

  const { mutate: cloneDesign } = useDataMutation({
    mutationFn: async (template: Template) => {
      const { error, data } = await supabase
        .from("layout")
        .insert({
          name: template.name,
          height: template.height,
          width: template.width,
          builder_data: template.builder_data,
          html: template.html,
          css: template.css,
          z_indexes: template.z_indexes,
          template_id: template.id,
          org_id: orgId,
        })
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to clone design",
    optimisticUpdateFn: (oldData: Template[] | undefined, template: Template) => {
      return [template, ...(oldData ?? [])]
    },
  })

  const { mutate: createLayout, isPending: isCreatingLayout } = useDataMutation({
    mutationFn: async (formData: InsertLayout) => {
      const { data, error } = await supabase
        .from("layout")
        .insert([
          {
            name: formData.name,
            width: formData.width,
            height: formData.height,
            org_id: orgId,
          },
        ])
        .select()
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Error creating layout",
    optimisticUpdateFn: (oldData, variables) => {
      if (!oldData) return oldData;
      return [...oldData, {
        ...undefinedToNull(variables),
        id: "optimistic-id-" + Date.now(),
        org_id: orgId ?? "",
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }];
    },
    options: {
      onSuccess: (data) => {
        log({
          eventtype: "LayoutCreated",
          name: "LayoutCreated",
          payload: {
            layoutId: data?.id ?? "undefined",
          },
        });
      },
      onError: (error, { name }) => {
        log({
          eventtype: "LayoutCreateFailed",
          name: "LayoutCreateFailed",
          payload: {
            layoutName: name ?? "undefined",
            errorMessage: error.message,
          },
        });
      },
    },
  });

  const { mutate: updateLayout, isPending: isUpdatingLayout } = useDataMutation({
    mutationFn: async (layout: UpdateLayout) => {
      const { data, error } = await supabase
        .from("layout")
        .update(layout)
        .match({ id: layout.id })
        .select();
      if (error) throw new Error(error.message);
      return data ? data[0] : null;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Error updating layout",
    optimisticUpdateFn: (oldData, layout) => {
      if (!oldData) return oldData;
      return oldData.map((l) =>
        l.id === layout.id
          ? { ...l, ...layout }
          : l
      );
    },
    options: {
      onSuccess: (layout) => {
        log({
          eventtype: "LayoutUpdated",
          name: "LayoutUpdated",
          payload: {
            layoutId: layout?.id,
            layoutName: layout?.name ?? "undefined",
          },
        })
      },
      onError: (error, layout) => {
        log({
          eventtype: "LayoutUpdateFailed",
          name: "LayoutUpdateFailed",
          payload: {
            layoutId: layout.id,
            layoutName: layout.name ?? "undefined",
            errorMessage: error.message,
          },
        });
      },
    },
  });

  const { mutate: deleteLayout, isPending: isDeletingLayout } = useDataMutation<string, Layout[], string>({
    mutationFn: async (layoutId: string) => {
      const { error } = await softDelete({
        table: "layout",
      }).match({ id: layoutId });
      if (error) throw new Error("Something went wrong");
      return layoutId;
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Something went wrong",
    optimisticUpdateFn: (oldData, deletedLayoutId) => {
      return oldData?.filter((layout) => layout.id !== deletedLayoutId);
    },
    options: {
      onSuccess: (layoutId) => {
        log({
          eventtype: "LayoutDeleted",
          name: "LayoutDeleted",
          payload: {
            layoutId: layoutId ?? "undefined",
          },
        });
      },
      onError: (error, layoutId) => {
        log({
          eventtype: "LayoutDeleteFailed",
          name: "LayoutDeleteFailed",
          payload: {
            layoutId,
            errorMessage: error.message,
          },
        });
      },
    },
  });

  return {
    layouts: layouts ?? [],
    layoutsFetchStatus,
    getLayouts,
    createLayout,
    isCreatingLayout,
    updateLayout,
    isUpdatingLayout,
    deleteLayout,
    isDeletingLayout,
    cloneDesign,
  }
};
